function uid(prefix = "q") {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/** 评估表副标题固定文案 */
export const ASSESSMENT_SUBTITLE =
  "仅供参考，以临床实际情况和医生判断为主";


export function createOption(label = "", remark = "") {
  return {
    label: String(label || "").trim(),
    remark: String(remark || "").trim(),
  };
}

export function createRemarkRule(columnIndex = 0, remark = "") {
  return {
    id: uid("rule"),
    type: "all_rows_equal",
    columnIndex: Number(columnIndex) || 0,
    remark: String(remark || "").trim(),
  };
}

export function normalizeOption(opt) {
  if (opt && typeof opt === "object") {
    return createOption(opt.label ?? opt.text ?? opt.name, opt.remark);
  }
  return createOption(opt, "");
}

export function normalizeOptions(list) {
  if (!Array.isArray(list) || !list.length) {
    return [createOption("选项A"), createOption("选项B")];
  }
  return list.map((item) => normalizeOption(item));
}

export function normalizeRemarkRules(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((rule) => {
      if (!rule || typeof rule !== "object") return null;
      return {
        id: String(rule.id || uid("rule")),
        type: "all_rows_equal",
        columnIndex: Number(rule.columnIndex) || 0,
        remark: String(rule.remark || "").trim(),
      };
    })
    .filter(Boolean);
}

export function normalizeQuestion(question) {
  const raw = question && typeof question === "object" ? question : {};
  const type = raw.type === "matrix" ? "matrix" : "single";
  if (type === "matrix") {
    const columns = Array.isArray(raw.columns) && raw.columns.length
      ? raw.columns.map((c) => String(c || "").trim())
      : ["良", "中", "差"];
    const rows = Array.isArray(raw.rows) && raw.rows.length
      ? raw.rows.map((r) => String(r || "").trim())
      : ["选项1", "选项2"];
    return {
      id: String(raw.id || uid()),
      type: "matrix",
      required: raw.required !== false,
      content: String(raw.content || ""),
      options: [],
      columns,
      rows,
      remarkRules: normalizeRemarkRules(raw.remarkRules),
    };
  }
  return {
    id: String(raw.id || uid()),
    type: "single",
    required: raw.required !== false,
    content: String(raw.content || ""),
    options: normalizeOptions(raw.options),
    columns: [],
    rows: [],
    remarkRules: [],
  };
}

export function createEmptyQuestion(type = "single") {
  if (type === "matrix") {
    return normalizeQuestion({
      type: "matrix",
      content: "",
      columns: ["良", "中", "差"],
      rows: ["选项1", "选项2"],
      remarkRules: [],
    });
  }
  return normalizeQuestion({
    type: "single",
    content: "",
    options: [createOption("选项A"), createOption("选项B")],
  });
}

export function getOptionLabel(opt) {
  return normalizeOption(opt).label;
}

export function getOptionRemark(opt) {
  return normalizeOption(opt).remark;
}

/** 单选：根据选中下标取备注 */
export function resolveSingleRemark(question, selectedIndex) {
  if (!question || selectedIndex == null || selectedIndex < 0) return "";
  const opt = (question.options || [])[selectedIndex];
  return getOptionRemark(opt);
}

/**
 * 矩阵：全部行选完后，匹配 remarkRules
 * answers: { [rowIndex]: columnIndex }
 */
export function resolveMatrixRemarks(question, answers) {
  if (!question || question.type !== "matrix") return [];
  const rows = question.rows || [];
  const rules = question.remarkRules || [];
  if (!rows.length || !rules.length) return [];

  const map = answers && typeof answers === "object" ? answers : {};
  const selected = [];
  for (let i = 0; i < rows.length; i += 1) {
    if (map[i] == null && map[String(i)] == null) return [];
    selected.push(Number(map[i] != null ? map[i] : map[String(i)]));
  }

  const remarks = [];
  rules.forEach((rule) => {
    if (rule.type !== "all_rows_equal") return;
    const col = Number(rule.columnIndex);
    if (selected.every((v) => v === col) && rule.remark) {
      remarks.push(rule.remark);
    }
  });
  return remarks;
}

function parseQuestions(raw) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
}

export function createEmptyForm(project = {}) {
  return {
    projectId: String(project.projectId || project.id || "").trim(),
    projectName: String(project.projectName || project.name || "").trim(),
    title: "术前信息评估表",
    subtitle: ASSESSMENT_SUBTITLE,
    notes: "",
    enabled: false,
    questions: [],
    updatedAt: null,
  };
}

export function normalizeForm(data, project = {}) {
  if (!data || typeof data !== "object") {
    return createEmptyForm(project);
  }
  return {
    projectId: String(
      data.projectId || data.project_id || project.projectId || project.id || ""
    ).trim(),
    projectName: String(
      data.projectName ||
        data.project_name ||
        project.projectName ||
        project.name ||
        ""
    ).trim(),
    title: String(data.title || "术前信息评估表"),
    subtitle: ASSESSMENT_SUBTITLE,
    notes: String(data.notes || ""),
    enabled:
      data.enabled === true || data.enabled === 1 || data.enabled === "1",
    questions: parseQuestions(data.questions).map((q) => normalizeQuestion(q)),
    updatedAt: data.updatedAt || data.updated_at || null,
  };
}

export function toSavePayload(form) {
  const payload = normalizeForm(form);
  return {
    projectId: String(payload.projectId),
    title: payload.title || "",
    subtitle: ASSESSMENT_SUBTITLE,
    notes: payload.notes || "",
    enabled: payload.enabled === true,
    questions: payload.questions,
  };
}
